import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Radio,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';

import { CrystalButton, EnhancedTable } from 'components';
import { roles, ROUTES } from 'consts';
import { usersTableColumns } from 'configs';
import { EUserRole } from 'enums';
import { useAppDispatch, useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { convertUsersToTableData } from 'utils';
import { useTeamMembers } from './team.hook';
import { useUpdateUserRoleMutation } from 'store/apis';
import { UserAssignedBuildingsDialog, UserUpdateDialog } from 'dialogs/user';
import { updateLayout } from 'store/slices/layout.slice';

type State = {
  userId: number | null;
  visibleUserUpdateDialog: boolean;
  visibleUserAssignedBuildingsDialog: boolean;
};

interface UsersPageProps {}

export const TeamPage: React.FC<UsersPageProps> = () => {
  const dispatch = useAppDispatch();
  const { isAdmin, orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [searchKeyword, setSearchKeyword] = React.useState<string>('');
  const [selectedRoles, setSelectedRoles] = React.useState<string[]>([EUserRole.BASIC, EUserRole.ADMIN]);
  const navigate = useNavigate();
  const [{ userId, visibleUserUpdateDialog, visibleUserAssignedBuildingsDialog }, setState] = React.useState<State>({
    userId: null,
    visibleUserUpdateDialog: false,
    visibleUserAssignedBuildingsDialog: false,
  });
  const [revokeUserAccess] = useUpdateUserRoleMutation();
  const [loading, teamMembers] = useTeamMembers({
    searchKeyword,
    searchRoles: selectedRoles,
    setState,
    revokeUserAccess: (userId: number) => revokeUserAccess({ orgSlug, userId, role: null }),
  });

  React.useEffect(() => {
    dispatch(updateLayout({ headerTitle: 'Team' }));
  }, []);

  const openAddUser = () => {
    navigate(`/${orgSlug}/${ROUTES.PROFILE}/${ROUTES.PROFILE_INVITE_USERS}`);
  };

  return (
    <>
      <Box sx={{ padding: 3 }}>
        <Grid container rowSpacing={5} columnSpacing={3}>
          <Grid item xs={12} container rowSpacing={3} columnSpacing={2}>
            <Grid item xs={12} sm={6} lg={5} xl={4}>
              <TextField
                label="Search by User"
                autoComplete="off"
                size="small"
                fullWidth
                InputProps={{ sx: { bgcolor: 'white' } }}
                value={searchKeyword}
                onChange={(event) => setSearchKeyword(event.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={4} xl={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Search by Role</InputLabel>
                <Select
                  multiple
                  value={selectedRoles}
                  onChange={(event: SelectChangeEvent<typeof selectedRoles>) => {
                    const {
                      target: { value },
                    } = event;
                    setSelectedRoles(typeof value === 'string' ? value.split(',') : value);
                  }}
                  input={<OutlinedInput label="Search by Role" size="small" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5 }}>
                      {selected.map((value, index) => (
                        <Chip
                          color="primary"
                          key={`selected-role-${index}`}
                          label={roles.find((item) => item.value === value)?.label || ''}
                          size="small"
                          sx={{ fontSize: 12, paddingX: 0.8 }}
                        />
                      ))}
                    </Box>
                  )}
                  sx={{ height: 40, bgcolor: 'white' }}
                >
                  {roles.map(({ value, label }, index) => (
                    <MenuItem key={`user-role-${index}`} value={value}>
                      <Radio checked={selectedRoles.indexOf(value) > -1} />
                      <ListItemText primary={label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item lg={3} xl={5} display={{ xs: 'none', lg: 'block' }} textAlign="right">
              <CrystalButton onClick={openAddUser} />
            </Grid>
          </Grid>

          <Grid item xs={12} container spacing={2}>
            <Grid item sx={{ flex: 1 }}>
              <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 500 }}>
                Team members are individuals that manage the cleancode subscription and have access to the cleancode
                portal.
              </Typography>
            </Grid>

            <Grid item display={{ xs: 'block', lg: 'none' }}>
              <CrystalButton onClick={openAddUser} />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <EnhancedTable
              config={{ toolbar: false, rowCheckable: false }}
              loading={loading}
              loadingLabel={'Loading users'}
              noDataLabel={'No user'}
              data={teamMembers}
              convertFunctionForDataToTableData={convertUsersToTableData(isAdmin)}
              headColumns={usersTableColumns}
              columnKeys={['userId', 'userName', 'EmployeeCode', 'Role', 'actions']}
              styles={{ tableStyle: { minWidth: 0 } }}
              initialRowsPerPage={10}
              visiblePagination
              rowActions={{}}
            />
          </Grid>
        </Grid>
      </Box>

      {typeof userId === 'number' && (
        <>
          <UserUpdateDialog
            visible={visibleUserUpdateDialog}
            setVisible={(visibleUserUpdateDialog: boolean) =>
              setState((prev) => ({ ...prev, visibleUserUpdateDialog }))
            }
            userId={userId}
          />

          <UserAssignedBuildingsDialog
            visible={visibleUserAssignedBuildingsDialog}
            setVisible={(visibleUserAssignedBuildingsDialog: boolean) =>
              setState((prev) => ({ ...prev, visibleUserAssignedBuildingsDialog }))
            }
            userId={userId}
          />
        </>
      )}
    </>
  );
};
